// Libraries
import axios from 'axios';
import {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';

//Components
import {Loader} from 'components/lib';
import Header from './parts/Header';
import Declined from './parts/Declined';
import Review from './parts/Review';
import Warning from './parts/Warning';
import Message from './parts/Message';
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";

//Helpers
import QuoteUtils from 'helpers/quoteUtils';
import {noFeeAgencies, Providers} from 'helpers/constants';
import {Errors, ErrorsMessages} from 'helpers/errors';

const quoteUtils = new QuoteUtils();

function normalizeCarrier(carrier) {
  if (!carrier) return [];
  if (Array.isArray(carrier)) return carrier;
  return [carrier];
}

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString();
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "N/A";
  return String(value);
};

const formatAddress = (...parts) => {
  const filtered = parts.filter((part) => part && String(part).trim());
  return filtered.length ? filtered.join(", ") : "N/A";
};

const InfoItem = ({ label, value }) => (
  <div>
    <div className="text-xs text-slate-500 uppercase">{label}</div>
    <div className="text-sm font-medium text-slate-900">{formatValue(value)}</div>
  </div>
);

const SectionCard = ({ title, children }) => (
  <Card className="border-slate-200">
    <CardHeader>
      <CardTitle className="text-lg">{title}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

export function ReviewQuote() {
  const { state } = useLocation();
  const { data } = state;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [quote, setQuote] = useState({});
  const [declinedReason, setDeclinedReason] = useState(null);
  const [showLargeFleetModal, setShowLargeFleetModal] = useState(false);
  const [dismissedLargeFleetModal, setDismissedLargeFleetModal] = useState(false);
  const [liveDataUnavailable, setLiveDataUnavailable] = useState(false);

  const carriers = normalizeCarrier(quote.carrier ?? data?.carrier);
  const policyTypeRaw =
    quote?.policy_type ?? data?.policy_type ?? quote?.quote?.step1?.quoteType ?? "";
  const policyType = String(policyTypeRaw).toLowerCase();
  const isIndividualQuote = policyType === "personal_auto" || policyType === "renters";
  const isRenters = policyType === "renters";
  const hasCoverWhale = carriers.includes("Cover Whale");
  const hasOtherCarriers = carriers.some((c) => c !== "Cover Whale");
  const agentEmail = quote?.agent_email ?? data?.agent_email ?? "";
  const quoteId = quote?.id ?? data?.id ?? "";
  const formValues = quote?.quote ?? {};
  const hasFormValues = !!formValues?.step1;

  const isSubmissionInvalid =
    !loading && data?.submission_number === "N/A" && !quote?.id;
  const isDeclined = !loading && data?.status === "declined";
  const showErrorMessage =
    !loading && error === Errors.SOMETHING_WENT_WRONG;
  const isValid =
    !loading &&
    !isSubmissionInvalid &&
    !isDeclined &&
    !showErrorMessage;
  const vehicleCount =
    quote?.quote?.vehicles?.length ??
    quote?.number_of_vehicles ??
    data?.number_of_vehicles ??
    0;
  const shouldShowLargeFleetModal =
    isValid && !isIndividualQuote && vehicleCount >= 6;

  useEffect(() => {
    if (shouldShowLargeFleetModal && !dismissedLargeFleetModal) {
      setShowLargeFleetModal(true);
    }
  }, [shouldShowLargeFleetModal, dismissedLargeFleetModal]);

  async function getQuoteData() {
    if (!data) {
      setLoading(false);
      return {success: false, error: 'No data provided'};
    }

    try {
      const result = await Promise.allSettled([
        axios.get('/api/account'),
        axios.get(`/api/quote/${data?.id}`),
        data?.submission_number && data.submission_number !== "N/A"
          ? axios.get(`/api/quote/info/${data?.submission_number}`)
          : Promise.resolve({ data: { success: false } }),
      ]);

      const [accountRes, internalQuoteRes, coverwhaleRes] = result;
      const currentAgency = accountRes.value?.data?.data;
      const internalQuote = internalQuoteRes.value?.data?.data;
      const coverwhaleQuote =
        coverwhaleRes.status === 'fulfilled' ? coverwhaleRes.value?.data?.data : null;
      const hasLiveCoverWhaleData =
        coverwhaleRes.status === 'fulfilled' &&
        Boolean(coverwhaleQuote?.premiumFinanceDetails) &&
        coverwhaleQuote?.premiumFinanceDetails?.downPayment !== null &&
        coverwhaleQuote?.premiumFinanceDetails?.paymentAmount !== null &&
        coverwhaleQuote?.premiumFinanceDetails?.effectiveAPR !== null;

      if (internalQuote.status === 'declined') {
        setDeclinedReason(data.error_reason);
        setLoading(false);
        return {success: true};
      }

      if (!hasLiveCoverWhaleData) {
        setLiveDataUnavailable(true);
        setQuote({
          coverages: {},
          financialDetails: null,
          agent_email: internalQuote.agent_email,
          quote: internalQuote.quote,
          submission_number: internalQuote.submission_number || data.submission_number,
          id: data.id,
          status: internalQuote.status,
          effective_date: internalQuote.effective_date || data.effective_date,
          carrier: internalQuote.carrier,
          company_name: internalQuote.companyData?.name || '',
          policy_type: internalQuote.policy_type,
          number_of_vehicles: internalQuote.number_of_vehicles,
        });
        setLoading(false);
        return { success: true };
      }

      const rawCoverages = {
        ...coverwhaleQuote,
        towingLaborStorage: {
          value: Number(internalQuote?.quote?.limits?.limitTowingStorage),
          included: true,
        },
        waiverSubrogation: {
          included: internalQuote?.quote?.limits?.insuredWaiverSubrogation === 'Y',
        },
        UIIAintermodal: {
          included: internalQuote?.quote?.operations?.UIIAintermodal === 'Y',
        },
        primaryNoncontributoryAL: {
          included: internalQuote?.quote?.limits?.primaryNoncontributoryAL === 'Y',
        },
        primaryNoncontributoryTGL: {
          included: internalQuote?.quote?.limits?.primaryNoncontributoryTGL === 'Y',
        },
        trailerInterchange: {
          limit: internalQuote?.quote?.limits?.trailerInterchangeLimit,
          included: internalQuote?.quote?.limits?.trailerInterchange === 'Y',
        },
        commoditiesRefrigeration: {
          included: internalQuote?.quote?.commoditiesRefrigeration === 'Y',
        },
      };

      const {coverages, financialDetails} = quoteUtils.calculate(
        rawCoverages,
        Providers.CoverWhale,
        noFeeAgencies.includes(currentAgency?.id)
      );

      if (financialDetails)
        await axios.patch(`/api/quote/${data?.id}`, {
          premium: Number(financialDetails?.premium),
        });

      setLiveDataUnavailable(false);
      setQuote({
        coverages,
        financialDetails,
        agent_email: internalQuote.agent_email,
        quote: internalQuote.quote,
        submission_number: internalQuote.submission_number || data.submission_number,
        id: data.id,
        status: internalQuote.status,
        effective_date: internalQuote.effective_date || data.effective_date,
        carrier: internalQuote.carrier,
        company_name: internalQuote.companyData?.name || '',
      });

      setLoading(false);
      return {success: true};

    } catch (error) {
      console.log(error);
      setLoading(false);
      return {
        success: false,
        error: error?.response?.status === 400
          ? Errors.SOMETHING_WENT_WRONG
          : Errors.DATA_NOT_READY,
      };
    }
  }

  async function runQuoteHandler() {
    const result = await getQuoteData();

    if (!result?.success) {
      setError(result?.error);
    }
  }


  useEffect(() => {
    runQuoteHandler();
  }, []);

  const otherCarriers = carriers.filter((c) => c !== "Cover Whale");
  const formatCarrierList = (list = []) => {
    if (!list.length) return "";
    if (list.length === 1) return list[0];
    if (list.length === 2) return list.join(" and ");
    return `${list.slice(0, -1).join(", ")}, and ${list.slice(-1)}`;
  };
  const carrierList = formatCarrierList(isIndividualQuote ? carriers : otherCarriers);
  const insuredName = `${formValues?.step2?.firstName || ""} ${
    formValues?.step2?.lastName || ""
  }`.trim();
  const insuredAddress = formatAddress(
    formValues?.step2?.address1,
    formValues?.step2?.address2,
    formValues?.step2?.city,
    formValues?.step2?.state,
    formValues?.step2?.zip
  );
  const priorAddress = formatAddress(
    formValues?.step2?.priorAddress,
    formValues?.step2?.priorCity,
    formValues?.step2?.priorState,
    formValues?.step2?.priorZip
  );
  const vehicles = formValues?.step3?.vehicles ?? [];
  const drivers = formValues?.step4?.drivers ?? [];
  const carriersDisplay = Array.isArray(formValues?.step1?.carrier)
    ? formValues.step1.carrier.join(", ")
    : formValues?.step1?.carrier || "N/A";
  return (
    <>
      {loading && <Loader />}
      {showLargeFleetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <Card className="w-full max-w-2xl border-amber-200 bg-white shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl text-amber-700">Attention!</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-base text-slate-700">
                This account qualifies for our Large Fleet Program.
                A Cover Badger representative will call you shortly to finish up this quote.
                Typically we can make price adjustments over the phone!
              </p>
              <div className="flex justify-end">
                <button
                  type="button"
                  className="rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
                  onClick={() => {
                    setShowLargeFleetModal(false);
                    setDismissedLargeFleetModal(true);
                  }}
                >
                  OK
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {showErrorMessage && (
        <Warning message={ErrorsMessages.SOMETHING_WENT_WRONG} />
      )}
      {isSubmissionInvalid && (
        <Warning
          message={ErrorsMessages.QUOTE_COVERWHALE_INVALID_SUBMISSION}
        />
      )}
      {isDeclined && <Declined reason={declinedReason} />}
      {isValid && (
        <>
          {liveDataUnavailable && (
            <Message className="mb-6">
              Live Cover Whale quote details are temporarily unavailable. Showing the saved quote snapshot instead.
            </Message>
          )}
          <Header data={quote} />
          {isIndividualQuote && (
            <Message className="text-xl !bg-yellow-300 w-full max-w-[1000px]">
              This quote will be submitted to: <strong>{carrierList || "N/A"}</strong> and you will receive correspondence by email to <strong>{agentEmail || "N/A"}</strong>. Your quote ID is <strong>{quoteId || "N/A"}</strong>, if you have any questions or need to submit an edit, please send an email to <strong>personallines@coverbadger.com</strong>.
            </Message>
          )}
          {isIndividualQuote && hasFormValues && (
            <div className="w-full max-w-[1000px] mx-auto space-y-6">
              <SectionCard title="Summary">
                <div className="grid gap-4 md:grid-cols-2">
                  <InfoItem label="Producer" value={formValues?.step1?.producer} />
                  <InfoItem label="Agent Email" value={formValues?.step1?.agentEmail} />
                  <InfoItem
                    label="Policy Effective Date"
                    value={formatDate(formValues?.step1?.policyDate)}
                  />
                  <InfoItem label="Insured State" value={formValues?.step1?.insuredState} />
                  <InfoItem label="Carriers" value={carriersDisplay} />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <div className="text-xs text-slate-500 uppercase">Insured</div>
                    <div className="text-sm font-medium text-slate-900">
                      {insuredName || "N/A"}
                    </div>
                    <div className="text-xs text-slate-500">
                      {formValues?.step2?.email || "N/A"}
                    </div>
                  </div>
                  {!isRenters && (
                    <>
                      <InfoItem label="Vehicles" value={vehicles.length} />
                      <InfoItem label="Drivers" value={drivers.length} />
                    </>
                  )}
                  {isRenters && (
                    <>
                      <InfoItem label="Residence Type" value={formValues?.step2?.residenceType} />
                      <InfoItem
                        label="Personal Property"
                        value={formValues?.step2?.personalPropertyAmount}
                      />
                    </>
                  )}
                </div>
              </SectionCard>

              <SectionCard title="Insured Details">
                <div className="grid gap-4 md:grid-cols-2">
                  <InfoItem label="Name" value={insuredName || "N/A"} />
                  <InfoItem
                    label="Date of Birth"
                    value={formatDate(formValues?.step2?.dateOfBirth)}
                  />
                  <InfoItem label="Gender" value={formValues?.step2?.gender} />
                  <InfoItem label="Email" value={formValues?.step2?.email} />
                  <InfoItem label="Phone" value={formValues?.step2?.phone} />
                  <InfoItem label="Address" value={insuredAddress} />
                  <InfoItem
                    label="Moved Recently"
                    value={formValues?.step2?.movedRecently}
                  />
                  <InfoItem
                    label="Prior Address"
                    value={formValues?.step2?.movedRecently ? priorAddress : "N/A"}
                  />
                </div>
              </SectionCard>

              {!isRenters && (
                <SectionCard title="Vehicles">
                  {vehicles.length ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      {vehicles.map((vehicle, index) => {
                        const primaryUse =
                          vehicle?.primaryUse === "Other"
                            ? `${vehicle?.primaryUse} - ${vehicle?.primaryUseOther || "N/A"}`
                            : vehicle?.primaryUse;
                        return (
                          <Card key={`vehicle-${index}`} className="border-slate-200">
                            <CardHeader>
                              <CardTitle className="text-base">
                                Vehicle {index + 1}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="grid gap-3 md:grid-cols-2">
                              <InfoItem label="Year" value={vehicle?.year} />
                              <InfoItem label="Make" value={vehicle?.make} />
                              <InfoItem label="Model" value={vehicle?.model} />
                              <InfoItem label="VIN" value={vehicle?.vin} />
                              <InfoItem label="Garaging Zip" value={vehicle?.garagingZip} />
                              <InfoItem label="Primary Use" value={primaryUse} />
                              <InfoItem label="Annual Miles" value={vehicle?.annualMiles} />
                              <InfoItem label="Ride Share" value={vehicle?.rideShare} />
                              <InfoItem label="Delivery" value={vehicle?.delivery} />
                              <InfoItem label="Lienholder" value={vehicle?.lienholder} />
                              <InfoItem
                                label="APD Deductible Selected"
                                value={vehicle?.apdCoverage}
                              />
                              <InfoItem
                                label="APD Deductible Amount"
                                value={vehicle?.apdCoverage ? vehicle?.apdDeductible : "N/A"}
                              />
                              <InfoItem label="Lienholder Name" value={vehicle?.lienholderName} />
                              <InfoItem
                                label="Lienholder Address"
                                value={formatAddress(
                                  vehicle?.lienholderAddress,
                                  vehicle?.lienholderAddress2,
                                  vehicle?.lienholderCity,
                                  vehicle?.lienholderState,
                                  vehicle?.lienholderZip
                                )}
                              />
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No vehicles added.</div>
                  )}
                </SectionCard>
              )}

              {!isRenters && (
                <SectionCard title="Drivers">
                  {drivers.length ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      {drivers.map((driver, index) => {
                        const driverName = `${driver?.firstName || ""} ${
                          driver?.lastName || ""
                        }`.trim();
                        return (
                          <Card key={`driver-${index}`} className="border-slate-200">
                            <CardHeader>
                              <CardTitle className="text-base">
                                Driver {index + 1}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="grid gap-3 md:grid-cols-2">
                              <InfoItem label="Name" value={driverName || "N/A"} />
                              <InfoItem
                                label="Date of Birth"
                                value={formatDate(driver?.dateOfBirth)}
                              />
                              <InfoItem label="Gender" value={driver?.gender} />
                              <InfoItem label="Marital Status" value={driver?.maritalStatus} />
                              <InfoItem label="Education" value={driver?.educationLevel} />
                              <InfoItem label="License Type" value={driver?.licenseType} />
                              <InfoItem
                                label="Licensed Duration"
                                value={driver?.licensedDuration}
                              />
                              <InfoItem label="Employment Type" value={driver?.employmentType} />
                              <InfoItem label="Employment Line" value={driver?.employmentLine} />
                              <InfoItem label="Driver Status" value={driver?.autoDriverStatus} />
                              <InfoItem label="SR-22" value={driver?.sr22} />
                              <InfoItem
                                label="Distant Student Discount"
                                value={driver?.distantStudentDiscount}
                              />
                              <InfoItem
                                label="Good Student Discount"
                                value={driver?.goodStudentDiscount}
                              />
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No drivers added.</div>
                  )}
                </SectionCard>
              )}

              {!isRenters && (
                <SectionCard title="Insured History">
                  <div className="grid gap-4 md:grid-cols-2">
                    <InfoItem
                      label="Prior Insurance Status"
                      value={formValues?.step5?.priorInsuranceStatus}
                    />
                    <InfoItem
                      label="Prior Carrier"
                      value={formValues?.step5?.priorInsuranceCarrier}
                    />
                    <InfoItem
                      label="Bodily Injury Limits"
                      value={formValues?.step5?.bodilyInjuryLimits}
                    />
                    <InfoItem
                      label="Years With Carrier"
                      value={formValues?.step5?.yearsWithCarrier}
                    />
                    <InfoItem label="Drivers Listed" value={formValues?.step5?.driversListed} />
                    <InfoItem label="PIP Claims (5 yrs)" value={formValues?.step5?.pipClaims} />
                    <InfoItem
                      label="Total Residents"
                      value={formValues?.step5?.totalResidents}
                    />
                    <InfoItem
                      label="Primary Residence"
                      value={formValues?.step5?.primaryResidence}
                    />
                    <InfoItem
                      label="Renters Discount"
                      value={formValues?.step5?.rentersDiscount}
                    />
                  </div>
                </SectionCard>
              )}

              {isRenters && (
                <SectionCard title="Renters History">
                  <div className="grid gap-4 md:grid-cols-2">
                    <InfoItem label="Prior Carrier" value={formValues?.step3?.priorCarrier} />
                    <InfoItem
                      label="Prior Liability Limit"
                      value={formValues?.step3?.priorLiabilityLimit}
                    />
                    <InfoItem
                      label="Reported Claims"
                      value={formValues?.step3?.reportedClaims}
                    />
                  </div>
                </SectionCard>
              )}

              {isRenters && (
                <SectionCard title="Renters Details">
                  <div className="grid gap-4 md:grid-cols-2">
                    <InfoItem label="Residence Type" value={formValues?.step2?.residenceType} />
                    <InfoItem
                      label="Personal Property Amount"
                      value={formValues?.step2?.personalPropertyAmount}
                    />
                    <InfoItem
                      label="Restricted Dogs"
                      value={formValues?.step2?.hasRestrictedDogs}
                    />
                  </div>
                </SectionCard>
              )}

              {isRenters && (
                <SectionCard title="Demographic">
                  <div className="grid gap-4 md:grid-cols-2">
                    <InfoItem
                      label="First Name"
                      value={formValues?.step2?.demographicFirstName}
                    />
                    <InfoItem
                      label="Last Name"
                      value={formValues?.step2?.demographicLastName}
                    />
                    <InfoItem
                      label="Marital Status"
                      value={formValues?.step2?.demographicMaritalStatus}
                    />
                    <InfoItem
                      label="Date of Birth"
                      value={formatDate(formValues?.step2?.demographicDateOfBirth)}
                    />
                    <InfoItem label="Gender" value={formValues?.step2?.demographicGender} />
                  </div>
                </SectionCard>
              )}

              {isRenters && (
                <SectionCard title="Additional Person">
                  <div className="grid gap-4 md:grid-cols-2">
                    <InfoItem
                      label="First Name"
                      value={formValues?.step2?.additionalFirstName}
                    />
                    <InfoItem
                      label="Last Name"
                      value={formValues?.step2?.additionalLastName}
                    />
                    <InfoItem
                      label="Marital Status"
                      value={formValues?.step2?.additionalMaritalStatus}
                    />
                    <InfoItem
                      label="Relationship"
                      value={formValues?.step2?.additionalRelationship}
                    />
                    <InfoItem
                      label="Second Named Insured"
                      value={formValues?.step2?.additionalSecondNamedInsured}
                    />
                    <InfoItem
                      label="Date of Birth"
                      value={formatDate(formValues?.step2?.additionalDateOfBirth)}
                    />
                    <InfoItem label="Gender" value={formValues?.step2?.additionalGender} />
                  </div>
                </SectionCard>
              )}

              {!isRenters && (
                <SectionCard title="Coverage Highlights">
                  <div className="grid gap-4 md:grid-cols-2">
                    <InfoItem
                      label="Bodily Injury & Property Damage"
                      value={formValues?.step6?.bodilyInjury}
                    />
                    <InfoItem
                      label="UM/UIM Property Damage"
                      value={formValues?.step6?.umPropertyDamage}
                    />
                    <InfoItem label="Med Pay" value={formValues?.step6?.medPay} />
                    <InfoItem label="PIP" value={formValues?.step6?.pip} />
                    <InfoItem
                      label="Comp Deductible"
                      value={formValues?.step6?.compDeductible}
                    />
                    <InfoItem
                      label="Collision Deductible"
                      value={formValues?.step6?.collisionDeductible}
                    />
                    <InfoItem label="Payment Plan" value={formValues?.step6?.paymentPlan} />
                    <InfoItem label="Run Clue/MVR" value={formValues?.step6?.runClue} />
                  </div>
                </SectionCard>
              )}

              {isRenters && (
                <SectionCard title="Coverage Highlights">
                  <div className="grid gap-4 md:grid-cols-2">
                    <InfoItem label="Liability Limit" value={formValues?.step4?.liabilityLimit} />
                    <InfoItem
                      label="Medical Payments Limit"
                      value={formValues?.step4?.medicalPaymentsLimit}
                    />
                    <InfoItem label="Deductible" value={formValues?.step4?.deductible} />
                    <InfoItem label="Theft Sublimit" value={formValues?.step4?.theftSublimit} />
                    <InfoItem
                      label="Home Computer Coverage"
                      value={formValues?.step4?.homeComputerCoverage}
                    />
                    <InfoItem label="Payment Plan" value={formValues?.step4?.paymentPlan} />
                    <InfoItem label="Run Clue" value={formValues?.step4?.runClue} />
                  </div>
                </SectionCard>
              )}
            </div>
          )}
          {!isIndividualQuote && quote?.agent_email?.length && hasOtherCarriers && (
            <Message className="text-xl !bg-yellow-300 w-full max-w-[1000px]">
              This quote will{hasCoverWhale ? " also " : " "}be submitted to: <strong>{carrierList}</strong> and you will receive correspondence by email to <strong>{quote?.agent_email}</strong>
            </Message>
          )}
          {hasCoverWhale && <Review data={quote} />}
        </>
      )}
    </>
  );
}
